import * as THREE from "three";
import { VfxFireworkExplosionBlastBatch } from "./vfx_firework_explosion_blast_batch.js";
import { VfxFireworkExplosionHeatBatch } from "./vfx_firework_explosion_heat_batch.js";
import { randomRange } from "../../../../common/common_funcs.js";
import { ResourceID } from "../../../../resource_loader/resource_id.js";
import { VfxErodingBillboardAdditiveBatch } from "../../../core/batches/vfx_eroding_billboard_batch.js";
import { IVfxTask } from "../../../core/runtime/vfx_task_runner.js";
import { VfxGradientAtlas } from "../../../core/textures/vfx_gradient_atlas.js";
import { IController, StopBehavior } from "../../../core/types/controller_types.js";
import { Vfx3DRuntime } from "../../../vfx_3d_runtime.js";
import { VfxBallisticStreakBatch } from "../../../core/batches/vfx_ballistic_streak_batch.js";

export class VfxFireworkExplosionTask implements IVfxTask, IController
{
  public is_active: boolean = true;
  private static gradient_id: number = -1;
  private static sparks_gradient_id: number = -1;

  private position: THREE.Vector3;
  private start_time: number;
  private has_spawned: boolean = false;

  // --- Explosion Blast Constants ---
  private static readonly BLAST_START_SCALE            : number        = 0.6;
  private static readonly BLAST_END_SCALE              : number        = 1.6;
  private static readonly BLAST_LIFESPAN               : number        = 0.6;
  private static readonly BLAST_DISSOLVE_START_VALUE   : number        = 0.4; // Напіврозчинена в момент спавну (0.0 = ціла, 0.5 = напіврозчинена, 1.0 = повністю розчинена)
  private static readonly BLAST_DISSOLVE_END_VALUE     : number        = 1.0; // Повне розчинення в кінці життя
  private static readonly BLAST_DISSOLVE_START_PROGRESS: number        = 0.4; // Момент часу (0.0..1.0), коли сфера починає розчинятися далі
  private static readonly BLAST_ROTATION_AXIS          : THREE.Vector3 = new THREE.Vector3( 1, 0, 0 ); // Рух текстури вверх (обертання навколо горизонтальної осі X)
  private static readonly BLAST_ROTATION_SPEED         : number        = 4.0;

  // --- Explosion Heat Constants (Match3 style, 3D additive) ---
  private static readonly HEAT_BURST_COUNT     : number = 10;
  private static readonly HEAT_DELAY           : number = 0.05;
  private static readonly HEAT_LIFESPAN        : number = 0.7;
  private static readonly HEAT_SPAWN_RADIUS    : number = 0.6;
  private static readonly HEAT_START_SCALE     : number = 0.4;
  private static readonly HEAT_END_SCALE       : number = 2;
  private static readonly HEAT_SPEED           : number = 1.2;

  // --- Explosion Fire Particles Constants (ResourceID.Fire + CloudNoiseTexture) ---
  private static readonly FIRE_BURST_COUNT              : number = 20;
  private static readonly FIRE_DELAY                    : number = 0.0;
  private static readonly FIRE_LIFESPAN                 : number = 0.45;
  private static readonly FIRE_SPAWN_RADIUS             : number = 0.3;
  private static readonly FIRE_START_SCALE              : number = 0.3;
  private static readonly FIRE_END_SCALE                : number = 1.3;
  private static readonly FIRE_SPEED                    : number = 3.2;
  private static readonly FIRE_DISSOLVE_START_PROGRESS : number      = 0.2; // Момент часу (0.0..1.0), коли частинки вогню починають розчинятися через CloudNoise
  private static readonly FIRE_COLOR_1                 : THREE.Color = new THREE.Color( 0xFFDC50 );
  private static readonly FIRE_COLOR_2                 : THREE.Color = new THREE.Color( 0xFF8C3C );
  private static readonly FIRE_END_RATIO               : number      = 0.6;
  private static readonly FIRE_DISSOLVE_SOFTNESS       : number      = 0.6;

  // --- Parabolic Sparks Constants ---
  private static readonly SPARKS_BURST_COUNT         : number      = 20;
  private static readonly SPARKS_LIFESPAN            : number      = 0.6;
  private static readonly SPARKS_COLOR_1             : THREE.Color = new THREE.Color( 0xFFFFC8 );
  private static readonly SPARKS_COLOR_2             : THREE.Color = new THREE.Color( 0xFFB450 );
  private static readonly SPARKS_COLOR_3             : THREE.Color = new THREE.Color( 0xFF5014 );
  private static readonly SPARKS_COLOR_2_PROGRESS    : number      = 0.5;
  private static readonly SPARKS_COLOR_3_PROGRESS    : number      = 0.7;
  private static readonly SPARKS_MIN_SPEED           : number      = 6;
  private static readonly SPARKS_MAX_SPEED           : number      = 8;
  private static readonly SPARKS_UP_MIN              : number      = 2.0;
  private static readonly SPARKS_UP_MAX              : number      = 4.0;
  private static readonly SPARKS_GRAVITY             : number      = 8.0;
  private static readonly SPARKS_MIN_WIDTH           : number      = 0.4;
  private static readonly SPARKS_MAX_WIDTH           : number      = 0.7;
  private static readonly SPARKS_MIN_HEIGHT          : number      = 0.5;
  private static readonly SPARKS_MAX_HEIGHT          : number      = 0.9;
  private static readonly SPARKS_STRETCH_FACTOR      : number      = 0.05;
  private static readonly SPARKS_SHRINK_START_RATIO  : number      = 0.5;
  private static readonly SPARKS_SPAWN_AREA_RADIUS   : number      = 0.4;

  public constructor( position: THREE.Vector3 )
  {
    this.position   = position.clone();
    this.start_time = Vfx3DRuntime.current_time;
  }

  public setPosition( pos: THREE.Vector3 ): void
  {
    this.position.copy( pos );
  }

  public stop( behavior?: StopBehavior ): void
  {
    this.is_active = false;
  }

  public start(): void
  {
    this.is_active = true;
  }

  public accelerate( factor: number ): void {}

  public onUpdate( now: number ): boolean
  {
    if ( !this.is_active )
      return true;
    if ( now < this.start_time )
      return false;

    const age = now - this.start_time;

    if ( !this.has_spawned )
    {
      this.has_spawned = true;
      this.spawnSphere();
      this.spawnFire();
      this.spawnSmog();
      this.spawnSparks();
    }

    const max_lifespan = Math.max(
      VfxFireworkExplosionTask.BLAST_LIFESPAN,
      VfxFireworkExplosionTask.FIRE_DELAY + VfxFireworkExplosionTask.FIRE_LIFESPAN,
      VfxFireworkExplosionTask.SPARKS_LIFESPAN,
      VfxFireworkExplosionTask.HEAT_DELAY + VfxFireworkExplosionTask.HEAT_LIFESPAN
    );

    if ( age > max_lifespan )
      return true; // Done

    return false;
  }

  private static readonly scratch_dir = new THREE.Vector3();
  private static readonly scratch_pos = new THREE.Vector3();
  private static readonly scratch_vel = new THREE.Vector3();
  private static readonly scratch_size = new THREE.Vector2();

  private spawnSphere(): void
  {
    const sphere_batch = Vfx3DRuntime.getBatch( VfxFireworkExplosionBlastBatch );
    sphere_batch.spawnSphere(
      this.position,
      VfxFireworkExplosionTask.BLAST_START_SCALE,
      VfxFireworkExplosionTask.BLAST_END_SCALE,
      this.start_time,
      VfxFireworkExplosionTask.BLAST_LIFESPAN,
      VfxFireworkExplosionTask.BLAST_DISSOLVE_START_VALUE,
      VfxFireworkExplosionTask.BLAST_DISSOLVE_END_VALUE,
      VfxFireworkExplosionTask.BLAST_DISSOLVE_START_PROGRESS,
      VfxFireworkExplosionTask.BLAST_ROTATION_AXIS,
      VfxFireworkExplosionTask.BLAST_ROTATION_SPEED
    );
  }

  private spawnFire(): void
  {
    const fire_batch = Vfx3DRuntime.getBatch( VfxErodingBillboardAdditiveBatch );
    const count = VfxFireworkExplosionTask.FIRE_BURST_COUNT;
    const spawn_time = this.start_time + VfxFireworkExplosionTask.FIRE_DELAY;

    if ( VfxFireworkExplosionTask.gradient_id === -1 ) {
      VfxFireworkExplosionTask.gradient_id = VfxGradientAtlas.registerGradient([
        { stop: 0.0, color: VfxFireworkExplosionTask.FIRE_COLOR_1 },
        { stop: VfxFireworkExplosionTask.FIRE_END_RATIO, color: VfxFireworkExplosionTask.FIRE_COLOR_2 },
        { stop: 1.0, color: new THREE.Color( 0x000000 ) }
      ]);
    }

    for ( let i = 0; i < count; i++ )
    {
      const u = Math.random() * 2.0 - 1.0;
      const theta = Math.random() * Math.PI * 2.0;
      const r = Math.sqrt( Math.max( 0.0, 1.0 - u * u ) );
      
      VfxFireworkExplosionTask.scratch_dir.set(
        r * Math.cos( theta ),
        u,
        r * Math.sin( theta )
      );

      const dist = Math.cbrt( Math.random() ) * VfxFireworkExplosionTask.FIRE_SPAWN_RADIUS;
      VfxFireworkExplosionTask.scratch_pos.copy( this.position ).addScaledVector( VfxFireworkExplosionTask.scratch_dir, dist );
      VfxFireworkExplosionTask.scratch_vel.copy( VfxFireworkExplosionTask.scratch_dir ).multiplyScalar( VfxFireworkExplosionTask.FIRE_SPEED );

      fire_batch.spawnParticle(
        ResourceID.Fire,
        ResourceID.CloudNoiseTexture,
        VfxFireworkExplosionTask.gradient_id,
        VfxFireworkExplosionTask.scratch_pos,
        VfxFireworkExplosionTask.scratch_vel,
        VfxFireworkExplosionTask.FIRE_START_SCALE,
        VfxFireworkExplosionTask.FIRE_END_SCALE,
        spawn_time,
        VfxFireworkExplosionTask.FIRE_LIFESPAN,
        VfxFireworkExplosionTask.FIRE_DISSOLVE_START_PROGRESS,
        VfxFireworkExplosionTask.FIRE_DISSOLVE_SOFTNESS,
        0.0
      );
    }
  }

  private spawnSmog(): void
  {
    const smog_batch = Vfx3DRuntime.getBatch( VfxFireworkExplosionHeatBatch );
    const count = VfxFireworkExplosionTask.HEAT_BURST_COUNT;
    const spawn_time = this.start_time + VfxFireworkExplosionTask.HEAT_DELAY;

    for ( let i = 0; i < count; i++ )
    {
      const u = Math.random() * 2.0 - 1.0;
      const theta = Math.random() * Math.PI * 2.0;
      const r = Math.sqrt( Math.max( 0.0, 1.0 - u * u ) );
      
      VfxFireworkExplosionTask.scratch_dir.set(
        r * Math.cos( theta ),
        u,
        r * Math.sin( theta )
      );

      const dist = Math.cbrt( Math.random() ) * VfxFireworkExplosionTask.HEAT_SPAWN_RADIUS;
      VfxFireworkExplosionTask.scratch_pos.copy( this.position ).addScaledVector( VfxFireworkExplosionTask.scratch_dir, dist );
      VfxFireworkExplosionTask.scratch_vel.copy( VfxFireworkExplosionTask.scratch_dir ).multiplyScalar( VfxFireworkExplosionTask.HEAT_SPEED );
      const rotation = Math.random() * Math.PI * 2;

      smog_batch.spawnParticle(
        VfxFireworkExplosionTask.scratch_pos,
        VfxFireworkExplosionTask.scratch_vel,
        VfxFireworkExplosionTask.HEAT_START_SCALE,
        VfxFireworkExplosionTask.HEAT_END_SCALE,
        spawn_time,
        VfxFireworkExplosionTask.HEAT_LIFESPAN,
        rotation
      );
    }
  }

  private spawnSparks(): void
  {
    const sparks_batch = Vfx3DRuntime.getBatch( VfxBallisticStreakBatch );

    if ( VfxFireworkExplosionTask.sparks_gradient_id === -1 )
    {
      VfxFireworkExplosionTask.sparks_gradient_id = VfxGradientAtlas.registerGradient([
        { stop: 0.0, color: VfxFireworkExplosionTask.SPARKS_COLOR_1 },
        { stop: VfxFireworkExplosionTask.SPARKS_COLOR_2_PROGRESS, color: VfxFireworkExplosionTask.SPARKS_COLOR_2 },
        { stop: VfxFireworkExplosionTask.SPARKS_COLOR_3_PROGRESS, color: VfxFireworkExplosionTask.SPARKS_COLOR_3 },
        { stop: 1.0, color: new THREE.Color( 0x000000 ) }
      ]);
    }

    const count = VfxFireworkExplosionTask.SPARKS_BURST_COUNT;
    const golden_angle = Math.PI * ( 3.0 - Math.sqrt( 5.0 ) );
    const random_rot = Math.random() * Math.PI * 2;

    for ( let i = 0; i < count; i++ )
    {
      const y = 1.0 - ( ( i + 0.5 ) / count ) * 2.0;
      const radius = Math.sqrt( Math.max( 0.0, 1.0 - y * y ) );
      const phi = i * golden_angle + random_rot;

      VfxFireworkExplosionTask.scratch_dir.set(
        Math.cos( phi ) * radius + randomRange( -0.15, 0.15 ),
        y + randomRange( -0.15, 0.15 ),
        Math.sin( phi ) * radius + randomRange( -0.15, 0.15 )
      ).normalize();

      const spawn_dist = randomRange( 0, VfxFireworkExplosionTask.SPARKS_SPAWN_AREA_RADIUS );
      VfxFireworkExplosionTask.scratch_pos.copy( this.position ).addScaledVector( VfxFireworkExplosionTask.scratch_dir, spawn_dist );

      const speed = randomRange(
        VfxFireworkExplosionTask.SPARKS_MIN_SPEED,
        VfxFireworkExplosionTask.SPARKS_MAX_SPEED
      );
      const up_speed = randomRange(
        VfxFireworkExplosionTask.SPARKS_UP_MIN,
        VfxFireworkExplosionTask.SPARKS_UP_MAX
      );
      VfxFireworkExplosionTask.scratch_vel.copy( VfxFireworkExplosionTask.scratch_dir ).multiplyScalar( speed );
      VfxFireworkExplosionTask.scratch_vel.y += up_speed;

      const width = randomRange(
        VfxFireworkExplosionTask.SPARKS_MIN_WIDTH,
        VfxFireworkExplosionTask.SPARKS_MAX_WIDTH
      );
      const height = randomRange(
        VfxFireworkExplosionTask.SPARKS_MIN_HEIGHT,
        VfxFireworkExplosionTask.SPARKS_MAX_HEIGHT
      );
      VfxFireworkExplosionTask.scratch_size.set( width, height );

      sparks_batch.spawnParticle(
        ResourceID.EllipseGlow1,
        VfxFireworkExplosionTask.sparks_gradient_id,
        VfxFireworkExplosionTask.scratch_pos,
        VfxFireworkExplosionTask.scratch_vel,
        VfxFireworkExplosionTask.SPARKS_GRAVITY,
        VfxFireworkExplosionTask.scratch_size,
        VfxFireworkExplosionTask.SPARKS_STRETCH_FACTOR,
        VfxFireworkExplosionTask.SPARKS_SHRINK_START_RATIO,
        this.start_time,
        VfxFireworkExplosionTask.SPARKS_LIFESPAN
      );
    }
  }
}
